/**
 * MidiAuditionPlayer — standalone player for short, pre-extracted MIDI
 * windows. See `docs/spec/midi-quality-feedback.md` §4 and §5.2.
 *
 * Three surfaces use it: the "needs help" upload-modal audition (its original
 * home), the harmony scene's per-chord excerpt, and the catalogue sheet's 20s
 * preview. All three play a small, capped window (≤ 30s, ≤ 6 tracks, ≤ 200
 * notes / track) through an {@link AuditionSynth}; only the sound bank
 * differs. The player is **standalone** (no reuse of Lane / Scheduler /
 * Transport) for the reasons listed in the spec §5.2.
 *
 * Concurrency model
 * -----------------
 * Web Audio honors a schedule even after we stop reading from JS, so we
 * MUST NOT pre-arm 30s × N notes via `synth.noteOn(at)` up-front. Instead
 * we schedule in 200ms batches and tie every batch to a monotonically
 * incrementing `playId`. On `stop()` we:
 *   1. bump `playId` (every in-flight callback becomes a no-op),
 *   2. clear all pending setTimeout ids,
 *   3. call `synth.releaseAllNotes()` to cut the tail that was already
 *      scheduled on the WebAudio side during the previous batch.
 *
 * Lifecycle
 * ---------
 * The synth is an application-level singleton on both call sites. The player
 * intentionally does NOT dispose it in `dispose()`.
 */

import type { AuditionNote, AuditionTrack } from "@/lib/music/analyzer-types";

/**
 * Ce que le lecteur demande à un banc de sons, et rien de plus.
 *
 * Les appelants n'ont pas le même instrument : la modale d'import et la scène
 * d'harmonie passent par `GmSoundfontSynth`, qui suit le programme GM de chaque
 * piste ; l'extrait d'une fiche de catalogue passe par le piano de
 * `PianoAuditionSynth`, le même que le piano-roll. L'ordonnanceur, lui, est
 * identique — d'où cette frontière plutôt qu'une copie du lecteur.
 *
 * `gmProgram` et `isDrum` restent dans la signature : un banc mono-instrument
 * les ignore, il n'y a pas de raison d'amputer celui qui s'en sert.
 */
export interface AuditionSynth {
  ensureProgram(gmProgram: number): Promise<void>;
  ensureDrums(): Promise<void>;
  noteOn(
    midi: number,
    velocity: number,
    at: number | undefined,
    gmProgram: number,
    isDrum: boolean,
  ): void;
  noteOff(
    midi: number,
    at: number | undefined,
    gmProgram: number,
    isDrum: boolean,
  ): void;
  releaseAllNotes(): void;
}

/** How often the lookahead scheduler wakes up, in milliseconds. */
const TICK_MS = 25;

/**
 * How far ahead of the audio clock notes are committed to the WebAudio
 * timeline. Must comfortably exceed the worst-case timer jitter so a note is
 * never scheduled in the past.
 */
export const SCHEDULE_AHEAD_MS = 150;

/** Internal alias for a note tagged with the track it originates from. */
type ScheduledNote = AuditionNote & {
  gmProgram: number;
  isDrum: boolean;
};

export type AuditionState =
  | { kind: "loading" }
  | { kind: "idle" }
  | {
      kind: "playing";
      mode: "all" | "selection" | { trackIdx: number };
      /** AudioContext.currentTime captured at the moment playback started. */
      startedAt: number;
      /** Effective duration of the scheduled window, in milliseconds. */
      durationMs: number;
    }
  | { kind: "error"; message: string };

export class MidiAuditionPlayer {
  private readonly ctx: AudioContext;
  private readonly synth: AuditionSynth;
  private readonly tracks: AuditionTrack[];

  private state: AuditionState = { kind: "loading" };
  private readonly listeners = new Set<(s: AuditionState) => void>();

  /**
   * Monotonically increasing generation id. Every pending callback
   * captures the id at scheduling time and no-ops if the current id
   * has moved past it.
   */
  private playId = 0;

  /** Live `setTimeout` handles we may need to cancel on `stop()` / `dispose()`. */
  private readonly pendingTimeouts = new Set<ReturnType<typeof setTimeout>>();

  /**
   * The bounds of the running window when it is open-ended, `null` otherwise.
   *
   * Held on the instance rather than closed over by the scheduler so that
   * {@link extend} can move the end while the loop runs. A closed window
   * keeps its bounds in the loop's own arguments, as before: nothing can
   * change them, so nothing needs to read them from here.
   */
  private openWindow: { readonly startMs: number; endMs: number } | null = null;

  /** Cached flattened note streams keyed by mode for cheap restart. */
  private flatAllNotes: ScheduledNote[] | null = null;
  private readonly flatTrackNotes = new Map<number, ScheduledNote[]>();

  /**
   * Current selection-mix playback state: the set of audition track indices
   * being previewed and the song position (seconds) playback started from.
   * Used to restart the mix from the live playhead when the selection changes.
   */
  private currentSelection: number[] = [];
  private currentStartSec = 0;

  private readyPromise: Promise<void>;

  constructor(ctx: AudioContext, synth: AuditionSynth, tracks: AuditionTrack[]) {
    this.ctx = ctx;
    this.synth = synth;
    this.tracks = tracks;

    this.readyPromise = this.preload();
  }

  /**
   * Resolves once every GM program + drum bank present in the tracks
   * payload has been loaded (or failed). Uses `Promise.allSettled` so
   * a single CDN miss does not block the player.
   */
  whenReady(): Promise<void> {
    return this.readyPromise;
  }

  /**
   * Plays every track in parallel. If `durationSec` is omitted the
   * player runs until the last `noteOff` of the last note across all
   * tracks. Calling this while playing aborts the current playback.
   *
   * `openEnded` asks for a window whose end is **provisional**: it may be
   * pushed back later with {@link extend}, without a restart. See there.
   */
  playAll(
    startSec: number = 0,
    durationSec?: number,
    { openEnded = false }: { readonly openEnded?: boolean } = {},
  ): void {
    this.stop();

    const myPlayId = ++this.playId;
    const notes = this.getAllNotes();
    this.beginPlayback(myPlayId, notes, { kind: "all" }, startSec, durationSec, openEnded);
  }

  /**
   * Pushes the end of the running open-ended window back to `untilSec`
   * (absolute, in song seconds), keeping every voice as it sounds.
   *
   * The harmony scene plays the piece one gate at a time, and a gate crossed
   * while the previous segment is still sounding must not re-attack it: a
   * `stop()` + `playAll()` at the seam releases the held voices and skips
   * whatever was already committed to the audio clock, which is heard as a
   * hiccup on every gate — precisely the seam the grace period exists to
   * remove. Extending leaves the schedule alone and only moves the point
   * where it stops reading.
   *
   * Returns `false` when there is nothing to extend: no open-ended window is
   * running (it ended, or was never started). The caller then starts afresh.
   * An `untilSec` behind the current end is a no-op that still returns `true`.
   */
  extend(untilSec: number): boolean {
    const open = this.openWindow;

    if (open === null || this.state.kind !== "playing") {
      return false;
    }

    open.endMs = Math.max(open.endMs, untilSec * 1000);

    this.setState({
      ...this.state,
      durationMs: Math.max(0, open.endMs - open.startMs),
    });

    return true;
  }

  /**
   * Plays a single track in solo mode. The track index refers to the
   * position inside the `AuditionPayload.tracks` array (NOT the original
   * `InspectTrack.index`). Out-of-range indices fail fast.
   */
  playTrack(trackIdx: number, durationSec?: number): void {
    if (trackIdx < 0 || trackIdx >= this.tracks.length) {
      throw new RangeError(`playTrack: trackIdx ${trackIdx} out of range`);
    }

    this.stop();

    const myPlayId = ++this.playId;
    const notes = this.getTrackNotes(trackIdx);
    this.beginPlayback(myPlayId, notes, { kind: "track", trackIdx }, 0, durationSec);
  }

  /**
   * Plays the union of the given audition tracks as a live mix, from `fromSec`
   * seconds into the song to the end. This previews exactly what the user is
   * about to import. An empty selection stops playback (nothing to hear).
   */
  playSelection(trackIdxs: number[], fromSec: number = 0): void {
    this.stop();

    this.currentSelection = [...trackIdxs];
    this.currentStartSec = Math.max(0, fromSec);

    if (trackIdxs.length === 0) {
      return;
    }

    const myPlayId = ++this.playId;
    const notes = this.getSelectionNotes(trackIdxs);
    this.beginPlayback(myPlayId, notes, { kind: "selection" }, this.currentStartSec, undefined);
  }

  /**
   * Updates the selection mix live. When a selection is currently playing, the
   * mix restarts from the live playhead with the new set (a brief re-attack,
   * imperceptible enough for a preview); otherwise the new set is just stored
   * for the next `playSelection`.
   */
  updateSelection(trackIdxs: number[]): void {
    if (this.state.kind !== "playing" || this.state.mode !== "selection") {
      this.currentSelection = [...trackIdxs];

      return;
    }

    const playheadSec =
      this.currentStartSec + (this.ctx.currentTime - this.state.startedAt);
    this.playSelection(trackIdxs, playheadSec);
  }

  private getSelectionNotes(trackIdxs: number[]): ScheduledNote[] {
    const wanted = new Set(trackIdxs);
    const flat: ScheduledNote[] = [];

    this.tracks.forEach((t, idx) => {
      if (!wanted.has(idx)) {
        return;
      }

      for (const n of t.notes) {
        flat.push({
          midi: n.midi,
          startMs: n.startMs,
          durationMs: n.durationMs,
          velocity: n.velocity,
          gmProgram: t.gmProgram,
          isDrum: t.isDrumChannel,
        });
      }
    });

    flat.sort((a, b) => a.startMs - b.startMs);

    return flat;
  }

  /**
   * Hard stop. Bumps the generation id, drops every queued batch and
   * releases any voices already scheduled on the WebAudio side.
   * No-op on an already-idle player.
   */
  stop(): void {
    const wasPlaying = this.state.kind === "playing";

    this.playId += 1;

    for (const id of this.pendingTimeouts) {
      clearTimeout(id);
    }

    this.pendingTimeouts.clear();
    this.openWindow = null;
    this.synth.releaseAllNotes();

    if (wasPlaying) {
      this.setState({ kind: "idle" });
    }
  }

  isPlaying(): boolean {
    return this.state.kind === "playing";
  }

  /**
   * Subscribe to state transitions. The callback is fired synchronously
   * on every change but NOT for the current state. Returns an
   * unsubscribe function.
   */
  onStateChange(cb: (s: AuditionState) => void): () => void {
    this.listeners.add(cb);

    return () => {
      this.listeners.delete(cb);
    };
  }

  /**
   * Aggregated density across every track. When `buckets === 24` we
   * sum element-wise (zero-cost path). Otherwise we resample linearly
   * with weight conservation. No log scaling is applied here — that
   * concern lives in the renderer (`DensitySparkline`).
   */
  getDensityBuckets(buckets: number): number[] {
    if (buckets <= 0) {
      return [];
    }

    const aggregate = new Array<number>(24).fill(0);
    for (const t of this.tracks) {
      const src = t.densityBuckets;
      const limit = Math.min(24, src.length);

      for (let i = 0; i < limit; i++) {
        aggregate[i] += src[i];
      }
    }

    if (buckets === 24) {
      return aggregate;
    }

    return resampleBuckets(aggregate, buckets);
  }

  /**
   * Per-track density. Returns the raw 24-bucket array when
   * `buckets === 24`, otherwise resamples linearly.
   */
  getTrackDensityBuckets(trackIdx: number, buckets: number): number[] {
    if (buckets <= 0) {
      return [];
    }

    if (trackIdx < 0 || trackIdx >= this.tracks.length) {
      return new Array<number>(buckets).fill(0);
    }

    const src = this.tracks[trackIdx].densityBuckets;
    if (buckets === src.length) {
      return src.slice();
    }

    return resampleBuckets(src, buckets);
  }

  /**
   * Releases internal timers and listeners. Does NOT dispose the synth
   * (singleton). Idempotent.
   */
  dispose(): void {
    this.playId += 1;

    for (const id of this.pendingTimeouts) {
      clearTimeout(id);
    }

    this.pendingTimeouts.clear();
    this.synth.releaseAllNotes();
    this.listeners.clear();
  }

  // ── internals ───────────────────────────────────────────────

  private preload(): Promise<void> {
    const programs = new Set<number>();
    let needDrums = false;

    for (const t of this.tracks) {
      if (t.isDrumChannel) {
        needDrums = true;

        continue;
      }

      programs.add(t.gmProgram);
    }

    const proms: Promise<void>[] = [];
    for (const p of programs) {
      proms.push(this.synth.ensureProgram(p));
    }

    if (needDrums) {
      proms.push(this.synth.ensureDrums());
    }

    return Promise.allSettled(proms).then(() => {
      // Only transition to idle if we are still in loading; the user
      // may have called dispose() before the preload resolved.
      if (this.state.kind === "loading") {
        this.setState({ kind: "idle" });
      }
    });
  }

  private beginPlayback(
    myPlayId: number,
    notes: ScheduledNote[],
    mode: { kind: "all" } | { kind: "selection" } | { kind: "track"; trackIdx: number },
    startSec: number,
    durationSec: number | undefined,
    openEnded = false,
  ): void {
    // Best-effort resume — handles the suspended-then-user-gesture case
    // without prompting. The promise is intentionally ignored.
    void this.ctx.resume?.();

    const audioStart = this.ctx.currentTime;
    const windowStartMs = startSec * 1000;
    // Note timings are absolute (from the song start), so the window end is the
    // last note's absolute end, not an offset from `windowStartMs`.
    const windowEndMs =
      durationSec !== undefined
        ? windowStartMs + durationSec * 1000
        : computeTailMs(notes);

    const publicMode =
      mode.kind === "track" ? { trackIdx: mode.trackIdx } : mode.kind;

    this.openWindow = openEnded ? { startMs: windowStartMs, endMs: windowEndMs } : null;

    this.setState({
      kind: "playing",
      mode: publicMode,
      startedAt: audioStart,
      durationMs: Math.max(0, windowEndMs - windowStartMs),
    });

    this.scheduleLoop(myPlayId, notes, windowStartMs, windowEndMs, audioStart, 0);
  }

  /**
   * Lookahead scheduler anchored on the AudioContext clock (the "two clocks"
   * pattern). Every TICK_MS it schedules all not-yet-queued notes whose onset
   * falls within SCHEDULE_AHEAD_MS of the *audio* clock, then sleeps. Because
   * note times are committed to the WebAudio clock ahead of when they sound,
   * main-thread jank or `setTimeout` drift can no longer push a note into the
   * past (which previously caused the audible "jump"/late clumping).
   *
   * `idx` is the running cursor into the sorted `notes`: each note is scheduled
   * exactly once, when it first enters the lookahead horizon.
   *
   * ## Closed and open-ended windows
   *
   * A closed window is what every caller had: notes are clamped to its end,
   * so a long sustain cannot ring on, and once every note is queued the loop
   * only waits out the audible tail. Its bounds are the arguments.
   *
   * An open-ended window reads its end from `openWindow`, which {@link extend}
   * may push back at any tick. That changes three things. Onsets past the end
   * are **not consumed** — the cursor stops at the end, so notes that were
   * beyond it are still there when the end moves. Sustains are **not
   * clamped** — a voice committed to the audio clock cannot be un-clamped
   * later, and the window that ends up holding it is not known when it is
   * committed; instead, reaching the end releases every voice at once. And
   * the loop keeps ticking until the audio clock reaches the end rather than
   * until the notes run out, since the end can still move.
   */
  private scheduleLoop(
    playId: number,
    notes: ScheduledNote[],
    windowStartMs: number,
    closedEndMs: number,
    audioStart: number,
    idx: number,
  ): void {
    if (playId !== this.playId) {
      return;
    }

    const open = this.openWindow;
    const windowEndMs = open?.endMs ?? closedEndMs;
    const elapsedMs = (this.ctx.currentTime - audioStart) * 1000;
    const nowMs = windowStartMs + elapsedMs;
    const horizonMs =
      open === null ? nowMs + SCHEDULE_AHEAD_MS : Math.min(nowMs + SCHEDULE_AHEAD_MS, windowEndMs);

    let i = idx;

    while (i < notes.length && notes[i].startMs < horizonMs) {
      const n = notes[i];

      if (n.startMs >= windowStartMs && n.startMs < windowEndMs) {
        const onAt = audioStart + (n.startMs - windowStartMs) / 1000;
        // Clamp the note tail to the window: anything past the limit
        // is force-released so a long sustained note can't ring on.
        const rawEndMs = n.startMs + n.durationMs;
        const clampedEndMs = open === null ? Math.min(rawEndMs, windowEndMs) : rawEndMs;
        const offAt = audioStart + (clampedEndMs - windowStartMs) / 1000;

        this.synth.noteOn(n.midi, n.velocity, onAt, n.gmProgram, n.isDrum);
        this.synth.noteOff(n.midi, offAt, n.gmProgram, n.isDrum);
      }

      i += 1;
    }

    if (open !== null) {
      if (nowMs >= windowEndMs) {
        this.openWindow = null;
        this.synth.releaseAllNotes();
        this.setState({ kind: "idle" });

        return;
      }
    } else if (i >= notes.length) {
      // Everything is queued on the audio clock; wait out the audible tail
      // (anchored on the audio clock, not wall time) then go idle.
      const endAudioSec = audioStart + (windowEndMs - windowStartMs) / 1000;
      const remainingMs = Math.max(0, (endAudioSec - this.ctx.currentTime) * 1000);

      const tailTid = setTimeout(() => {
        this.pendingTimeouts.delete(tailTid);

        if (playId !== this.playId) {
          return;
        }

        this.setState({ kind: "idle" });
      }, remainingMs);
      this.pendingTimeouts.add(tailTid);

      return;
    }

    const tid = setTimeout(() => {
      this.pendingTimeouts.delete(tid);
      this.scheduleLoop(playId, notes, windowStartMs, closedEndMs, audioStart, i);
    }, TICK_MS);
    this.pendingTimeouts.add(tid);
  }

  private getAllNotes(): ScheduledNote[] {
    if (this.flatAllNotes !== null) {
      return this.flatAllNotes;
    }

    const flat: ScheduledNote[] = [];
    for (const t of this.tracks) {
      for (const n of t.notes) {
        flat.push({
          midi: n.midi,
          startMs: n.startMs,
          durationMs: n.durationMs,
          velocity: n.velocity,
          gmProgram: t.gmProgram,
          isDrum: t.isDrumChannel,
        });
      }
    }

    flat.sort((a, b) => a.startMs - b.startMs);
    this.flatAllNotes = flat;

    return flat;
  }

  private getTrackNotes(trackIdx: number): ScheduledNote[] {
    const cached = this.flatTrackNotes.get(trackIdx);
    if (cached !== undefined) {
      return cached;
    }

    const t = this.tracks[trackIdx];
    const flat: ScheduledNote[] = t.notes.map((n) => ({
      midi: n.midi,
      startMs: n.startMs,
      durationMs: n.durationMs,
      velocity: n.velocity,
      gmProgram: t.gmProgram,
      isDrum: t.isDrumChannel,
    }));

    // Per-track notes from the backend are already ordered, but we
    // re-sort defensively (the wire spec does not guarantee a stable
    // sort across analyzer versions).
    flat.sort((a, b) => a.startMs - b.startMs);
    this.flatTrackNotes.set(trackIdx, flat);

    return flat;
  }

  private setState(next: AuditionState): void {
    this.state = next;

    for (const cb of this.listeners) {
      cb(next);
    }
  }
}

/**
 * Time (in milliseconds) of the very last `noteOff` across the stream.
 * Returns 0 for an empty stream.
 */
function computeTailMs(notes: ScheduledNote[]): number {
  let tail = 0;

  for (const n of notes) {
    const end = n.startMs + n.durationMs;

    if (end > tail) {
      tail = end;
    }
  }

  return tail;
}

/**
 * Linear-weight resampling that preserves the total count. Used to
 * build the global density bar (typically 60 buckets) from the 24-bucket
 * per-track input. Pure utility, no rendering concerns (log scale lives
 * in `DensitySparkline`).
 */
function resampleBuckets(src: number[], target: number): number[] {
  if (target === src.length) {
    return src.slice();
  }

  const out = new Array<number>(target).fill(0);
  if (src.length === 0 || target === 0) {
    return out;
  }

  const ratio = src.length / target;

  for (let i = 0; i < target; i++) {
    const start = i * ratio;
    const end = (i + 1) * ratio;
    const startIdx = Math.floor(start);
    const endIdx = Math.min(src.length - 1, Math.floor(end - 1e-9));

    let acc = 0;
    for (let k = startIdx; k <= endIdx; k++) {
      const overlapStart = Math.max(start, k);
      const overlapEnd = Math.min(end, k + 1);
      const overlap = Math.max(0, overlapEnd - overlapStart);

      acc += src[k] * overlap;
    }

    out[i] = acc;
  }

  return out;
}
